import MondaySdk from "monday-sdk-js";
import { MondayClientSdk } from "monday-sdk-js/types/client-sdk.interface";
import { handleMondayError } from "./monday-handle-error";
import { MONDAY_API_VERSION } from "./monday-api-version";
import {
  getItemNamesQl,
  getWorkspacesQl,
  getAccountSizeQl,
  checkSubitemsQl,
  checkUpdatesQl,
  getStatusColumnDataQl,
  getPersonColumnDataQl,
  getFileColumnDataQl,
  getUsersQl,
  sendNotificationQl,
  createUpdateQl,
  createReplyQl,
  getUsersByUserIdQl,
  getTeamsByIdQl,
  getBoardDetailsQl,
  getBoardColumnsQl,
  getBoardAllColumnsQl,
  getColumnUpdateLogQl,
  batchUpdateLogQl,
  appSubscriptionQl,
  getWebhooksByBoardQl,
  deleteWebhookQl,
  installWebhookQl,
  getAuthorizedBoardUsersQl,
  getColumnDetailsByColumnIdQl,
  mutateAddColumnToBoardQl,
  renameColumnQl,
  createColumnQl,
  getBoardItemsQl,
  getItemsPageQl,
  getLastCreatedItemIdQl,
  getNextItemsPageQl,
  getGroupItemsPageQl,
  getNextGroupItemsPageQl,
  getItemColumnValuesQl,
  getColumnUpdateLogBatchQl,
  getAccessLevelQl,
  getTagsQl,
  getItemCurrentLocationsQl,
  getBoardNameQl,
  getDropdownLabelsQl,
  getEditableColumnStateQl,
  changeColumnValueQl,
  getBoardTagsQl,
  searchPeopleQl,
  getUsersByIdsQl,
  getMyTimeZoneQl,
  createOrGetTagQl,
} from "./monday-queries";
import logger from "../../lib/logger";
import { webhookEndpoints } from "../../data/constants/webhooks";
import { normalizeColumnType } from "../../utils/normalizeColumnType";
import { buildColumnDefaults } from "../../utils/columnDefaults";
import {
  BoardItemsResponse,
  BoardWithItems,
  ColumnValueType,
  ItemsPageSlice,
} from "./monday-query-interface";

/**
 * @class MondayService
 */
export class MondayService {
  /**
   * @param userToken
   * @private
   */
  public async getMondayServerSdkForUser(version?: string): Promise<MondayClientSdk> {
    try {
      // @ts-ignore
      const monday: MondayClientSdk = new MondaySdk();
      monday.setApiVersion(version || MONDAY_API_VERSION);

      return monday;
    } catch (error) {
      throw error;
    }
  }

  public async getItemNames(itemIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response = await sdkForUser.api(getItemNamesQl(itemIds));

      handleMondayError(response);
      return response;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async getWorkspaces() {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
      const response: any = await sdkForUser.api(getWorkspacesQl());

      handleMondayError(response);

      // Check if mainWorkspace exists and has the necessary properties
      const mainWorkspace = response?.data?.main_workspace?.[0]?.workspace;

      let mainWorkspaceEntry = null;
      if (mainWorkspace) {
        mainWorkspaceEntry = {
          id: mainWorkspace.id,
          name: mainWorkspace.name,
          color: mainWorkspace.settings?.icon?.color ?? "#0073ea",
          image: mainWorkspace.settings?.icon?.image ?? null,
        };
      }

      // Combine the arrays, including mainWorkspaceEntry only if it's not null
      const combinedWorkspaces = mainWorkspaceEntry
        ? [
            ...response.data.all_workspaces.filter(
              (workspace: any) => workspace.id !== mainWorkspaceEntry.id,
            ),
            mainWorkspaceEntry,
          ]
        : [...response.data.all_workspaces];

      const sortedWorkspaces = combinedWorkspaces.sort((a, b) => a.name.localeCompare(b.name));

      return sortedWorkspaces;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async getAccountSize() {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
      const response: any = await sdkForUser.api(getAccountSizeQl());

      handleMondayError(response);

      return response.data.boards;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async checkSubitems(itemIds: Number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(checkSubitemsQl(itemIds));

      handleMondayError(response);

      return response?.data?.items;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async checkUpdates(itemIds: Number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(checkUpdatesQl(itemIds));

      handleMondayError(response);

      return response?.data?.items;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async getStatusColumnData(itemIds: number[], columnId: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getStatusColumnDataQl(itemIds, columnId));

      handleMondayError(response);

      return response?.data;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async getPersonColumnData(itemIds: number[], columnId: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getPersonColumnDataQl(itemIds, columnId));

      handleMondayError(response);

      return response?.data;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async getFileColumnData(itemIds: number[], columnId: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getFileColumnDataQl(itemIds, columnId));

      handleMondayError(response);

      return response?.data;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async getUsers(pageSize: number, currentPage: number, searchTerm: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getUsersQl(pageSize, currentPage, searchTerm));

      handleMondayError(response);

      return response?.data?.users;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async getUsersByUserId(userIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getUsersByUserIdQl(userIds));

      handleMondayError(response);

      return response?.data?.users;
    } catch (error: any) {
      // monday's top-level message is always the constant "Graphql validation errors". errors[]
      // is the only thing naming the offending field, and without it this failure is
      // undiagnosable — a bare message here sent an incident down two wrong root causes.
      logger.error("Error fetching users by id", {
        message: error?.message,
        errors: error?.errors ?? error?.response?.errors,
        userIds,
      });
      throw error;
    }
  }

  public async getTeamsById(userIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getTeamsByIdQl(userIds));

      handleMondayError(response);

      return response?.data?.teams;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async sendNotification(userId: number, targetId: number, text: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(sendNotificationQl(userId, targetId, text));

      handleMondayError(response);
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async createUpdate(
    itemId: string,
    body: string,
    mentions: { id: string; type: "User" }[],
  ): Promise<{ id: string; body: string; text_body: string }> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
      const response: any = await sdkForUser.api(createUpdateQl(), {
        variables: { itemId, body, mentions },
      });
      handleMondayError(response);
      return response?.data?.create_update;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async createReply(
    parentId: string,
    body: string,
    mentions: { id: string; type: "User" }[],
  ): Promise<{ id: string; body: string; text_body: string }> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
      const response: any = await sdkForUser.api(createReplyQl(), {
        variables: { parentId, body, mentions },
      });
      handleMondayError(response);
      return response?.data?.create_update;
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async openLinkInTab(url: string) {
    try {
      const monday: MondayClientSdk = await this.getMondayServerSdkForUser();
      monday.execute("openLinkInTab", { url });
    } catch (error: any) {
      logger.error("Error opening link in tab", error);
      throw error;
    }
  }

  public async openFilesDialog(
    boardId: number,
    itemId: number,
    columnId: string,
    assetId: number | string,
  ) {
    try {
      const monday: MondayClientSdk = await this.getMondayServerSdkForUser();
      monday.execute("openFilesDialog", {
        boardId,
        itemId,
        columnId,
        assetId,
      });
    } catch (error: any) {
      logger.error("Error opening files dialog", error);
      throw error;
    }
  }

  public async getBoardDetails(boardIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getBoardDetailsQl(boardIds));

      handleMondayError(response);

      return response?.data?.boards;
    } catch (error: any) {
      logger.error("Error getting board details:", error);
      throw error;
    }
  }

  public async getBoardColumns(boardIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getBoardColumnsQl(boardIds));

      handleMondayError(response);

      return response?.data?.boards;
    } catch (error: any) {
      logger.error("Error getting board details:", error);
      throw error;
    }
  }

  public async getBoardAllColumns(boardIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getBoardAllColumnsQl(boardIds));

      handleMondayError(response);

      return response?.data?.boards;
    } catch (error: any) {
      logger.error("Error getting board columns:", error);
      throw error;
    }
  }

  public async getColumnValueByColumnId(items: any) {
    try {
      const query = `
            query {
              ${items
                .map(
                  (item: any, index: number) => `
                  item_${item.item_id}_column_values: items (ids: [${item.item_id}]) {
                    board {  
                      id  
                    }
                    id
                    column_values (ids: ${JSON.stringify(item.columnIds)}){
                      column  {
                        id
                        title
                      }
                      id
                      type
                      value
                      text
                    }
                  }
                `,
                )
                .join("\n")}
            }
            `;

      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(query);

      handleMondayError(response);
      return response?.data;
    } catch (error: any) {
      logger.error("Error fetching column value by column id", error);
      throw error;
    }
  }

  public async getColumnUpdateLog(
    itemId: number,
    columnId: string,
    boardId: number,
    limit?: number,
  ) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(
        getColumnUpdateLogQl(itemId, columnId, boardId, limit),
      );

      handleMondayError(response);
      return response;
    } catch (error: any) {
      logger.error("Error fetching column value by column id", error);
      throw error;
    }
  }

  public async getColumnUpdateLogBatch(boardIds: number[], itemIds: number[], columnIds: string[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(
        getColumnUpdateLogBatchQl(boardIds, itemIds, columnIds),
      );
      handleMondayError(response);
      return response?.data?.boards;
    } catch (error: any) {
      // Passing the raw error object serialises to {} through the Logtail transport, so the
      // errors[] array — the only part naming the offending field — never reached BetterStack.
      // Note this query selects activity_logs.user_id, which the sibling getColumnUpdateLogQl
      // does not; if that field is version-gated, this is where it would surface.
      logger.error("Error fetching column update log batch", {
        message: error?.message,
        errors: error?.errors ?? error?.response?.errors,
        boardIds,
        itemIds,
        columnIds,
      });
      throw error;
    }
  }

  public async batchUpdateLog(columns: any) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(batchUpdateLogQl(columns));

      handleMondayError(response);
      return response?.data;
    } catch (error: any) {
      logger.error("Error fetching column value by column id", error);
      throw error;
    }
  }

  public async openAppFeatureModal(
    urlPath?: string,
    urlParams?: any,
    height?: string,
    width?: string,
  ) {
    try {
      const monday: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response = await monday.execute("openAppFeatureModal", {
        urlPath,
        urlParams,
        height,
        width,
      });

      return response;
    } catch (error: any) {
      logger.error("Error opening app feature modal", error);
      throw error;
    }
  }

  public async closeAppFeatureModal() {
    try {
      const monday: MondayClientSdk = await this.getMondayServerSdkForUser();
      monday.execute("closeAppFeatureModal");
    } catch (error: any) {
      logger.error("Error closing app feature modal", error);
      throw error;
    }
  }
  public async getSubscriptionData() {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(appSubscriptionQl());

      handleMondayError(response);

      return response.data;
    } catch (error: any) {
      logger.error("Error fetching app subscription data", error);
      throw error;
    }
  }

  public async getWebhooksByBoard(boardId: number) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getWebhooksByBoardQl(boardId));

      handleMondayError(response);

      return response?.data?.webhooks;
    } catch (error: any) {
      logger.error("Error fetching webhooks", error);
      throw error;
    }
  }
  public async deleteWebhook(webhookId: number) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(deleteWebhookQl(webhookId));

      handleMondayError(response);
    } catch (error: any) {
      logger.error("Error deleting webhook", error);
      throw error;
    }
  }

  public async installWebhook(webhookEvent: string, boardId: number) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const url = webhookEndpoints[webhookEvent];

      const response: any = await sdkForUser.api(installWebhookQl(webhookEvent, boardId, url));

      if (!response?.data?.create_webhook) {
        throw new Error("Failed to install webhook, null response");
      }

      logger.log("Install Webhook Response", response);
    } catch (error: any) {
      logger.error("Error installing webhook", error);
      throw error;
    }
  }

  public async getAuthorizedBoardUsers(boardId: number) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getAuthorizedBoardUsersQl(boardId));

      handleMondayError(response);

      return response?.data?.boards[0]?.owners;
    } catch (error: any) {
      logger.error("Error fetching authorized users", error);
      throw error;
    }
  }

  public async getColumnDetailsByColumnId(boardId: number, columnIds: string[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getColumnDetailsByColumnIdQl(boardId, columnIds));

      handleMondayError(response);

      return response?.data?.boards[0]?.columns;
    } catch (error: any) {
      logger.error("Error fetching column details", error);
      throw error;
    }
  }

  public async addColumnToBoard(boardIds: number[], columnTitle: string, columnType: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(
        mutateAddColumnToBoardQl(boardIds, columnTitle, normalizeColumnType(columnType)),
      );

      handleMondayError(response);
    } catch (error: any) {
      logger.error("Error fetching column details", error);
      throw error;
    }
  }

  public async mondayToastNotification(
    message: string,
    type: "success" | "error" | "info",
    timeout: number,
  ) {
    try {
      const monday: MondayClientSdk = await this.getMondayServerSdkForUser();

      monday.execute("notice", {
        message: message,
        type: type,
        timeout: timeout,
      });
    } catch (error: any) {
      logger.error(error.message);
      throw error;
    }
  }

  public async renameColumn(boardId: number, columnId: string, newTitle: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(renameColumnQl(), {
        variables: { boardId, columnId, title: newTitle },
      });

      handleMondayError(response);

      return response;
    } catch (error: any) {
      logger.error("Error renaming column", error);
      throw error;
    }
  }

  public async createColumn(
    boardId: number,
    columnTitle: string,
    columnType: string,
    labels?: any,
  ) {
    // Key off the normalized type: the enum sent to monday is normalized, so
    // branching on the caller's raw type dropped defaults for legacy `color` boards.
    const normalizedColumnType = normalizeColumnType(columnType);
    const defaults = buildColumnDefaults(normalizedColumnType, labels);

    const variables = {
      boardId,
      title: columnTitle,
      columnType: normalizedColumnType,
      defaults: defaults ? JSON.stringify(defaults) : undefined,
    };

    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(createColumnQl(), { variables });

      handleMondayError(response);

      return response;
    } catch (error: any) {
      // monday's top-level message is always the constant "Graphql validation errors".
      // errors[] is the only thing that names the offending argument, so keep it —
      // logging error.message alone made this class of failure undiagnosable.
      logger.error("Error creating column", {
        message: error?.message,
        errors: error?.errors ?? error?.response?.errors,
        columnType: variables.columnType,
        defaults: variables.defaults,
      });
      throw error;
    }
  }

  public async getBoardItems(
    boardIds: number[],
    limit: number,
    columnIds?: string[] | null,
  ): Promise<BoardWithItems[] | undefined> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: BoardItemsResponse = await sdkForUser.api(
        getBoardItemsQl(boardIds, limit, columnIds),
      );

      handleMondayError(response);

      return response?.data?.boards;
    } catch (error: any) {
      logger.error("Error fetching board items", error);
      throw error;
    }
  }

  public async getItemsPage(
    boardId: number,
    limit: number,
    searchTerm?: string,
  ): Promise<ItemsPageSlice> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getItemsPageQl(boardId, limit, { searchTerm }));

      handleMondayError(response);

      const page = response?.data?.boards?.[0]?.items_page;
      return {
        cursor: page?.cursor ?? null,
        items: page?.items ?? [],
      };
    } catch (error: any) {
      logger.error("Error fetching items page", error);
      throw error;
    }
  }

  /**
   * The most-recently-created item id on a board, or null if the board has no
   * items / the query fails. Best-effort: used only for nudge deep-link targets,
   * so it must never throw into the caller.
   */
  public async getLastCreatedItemId(boardId: number): Promise<string | null> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getLastCreatedItemIdQl(boardId));

      handleMondayError(response);

      return response?.data?.boards?.[0]?.items_page?.items?.[0]?.id ?? null;
    } catch (error: any) {
      logger.error("Error fetching last created item", error);
      return null;
    }
  }

  /** One page of a group's items (id and name), for the merge picker. */
  public async getGroupItemsPage(
    boardId: number,
    groupId: string,
    limit: number,
    searchTerm?: string,
  ): Promise<{ cursor: string | null; items: Array<{ id: string; name: string }> }> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(
        getGroupItemsPageQl(boardId, groupId, limit, { searchTerm }),
      );

      handleMondayError(response);

      const page = response?.data?.boards?.[0]?.groups?.[0]?.items_page;
      return { cursor: page?.cursor ?? null, items: page?.items ?? [] };
    } catch (error: any) {
      logger.error("Error fetching group items page", error);
      throw error;
    }
  }

  public async getNextGroupItemsPage(
    cursor: string,
    limit: number,
  ): Promise<{ cursor: string | null; items: Array<{ id: string; name: string }> }> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getNextGroupItemsPageQl(cursor, limit));

      handleMondayError(response);

      const page = response?.data?.next_items_page;
      return { cursor: page?.cursor ?? null, items: page?.items ?? [] };
    } catch (error: any) {
      logger.error("Error fetching next group items page", error);
      throw error;
    }
  }

  public async getNextItemsPage(cursor: string, limit: number): Promise<ItemsPageSlice> {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getNextItemsPageQl(cursor, limit));

      handleMondayError(response);

      const page = response?.data?.next_items_page;
      return {
        cursor: page?.cursor ?? null,
        items: page?.items ?? [],
      };
    } catch (error: any) {
      logger.error("Error fetching next items page", error);
      throw error;
    }
  }

  public async getItemColumnValues(itemIds: number[], columnIds?: string[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getItemColumnValuesQl(itemIds));

      handleMondayError(response);

      return response?.data?.items;
    } catch (error: any) {
      logger.error("Error fetching item column values", error);
      throw error;
    }
  }

  /** Fetches current group (title) per item so UI reflects item moves across groups. */
  public async getItemCurrentLocations(itemIds: number[]) {
    if (!itemIds?.length) return [];
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
      const response: any = await sdkForUser.api(getItemCurrentLocationsQl(itemIds));
      handleMondayError(response);
      return response?.data?.items ?? [];
    } catch (error: any) {
      logger.error("Error fetching item current locations", error);
      throw error;
    }
  }

  public async getAccessLevel(boardIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser(MONDAY_API_VERSION);

      const response: any = await sdkForUser.api(getAccessLevelQl(boardIds));

      handleMondayError(response);

      return response?.data?.boards[0]?.access_level;
    } catch (error: any) {
      logger.error("Error fetching access level", error);
      throw error;
    }
  }

  public async getTags(columnIds: string[], itemIds: number[]) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();

      const response: any = await sdkForUser.api(getTagsQl(columnIds, itemIds));

      handleMondayError(response);

      return response;
    } catch (error: any) {
      logger.error("Error fetching tags", error);
      throw error;
    }
  }

  public async openIntegrationsModal(boardId: number, appFeatureId: number) {
    try {
      const monday: MondayClientSdk = await this.getMondayServerSdkForUser();
      monday.execute("openAppFeatureIntegrations", {
        boardId: boardId,
        appId: 10111107,
      });
    } catch (error: any) {
      logger.error("Error opening integrations modal", error);
      throw error;
    }
  }

  async getBoardName(boardId: number) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
      const response: any = await sdkForUser.api(getBoardNameQl(boardId));
      handleMondayError(response);
      return response?.data?.boards[0]?.name;
    } catch (error: any) {
      logger.error("Error fetching board name", error);
      throw error;
    }
  }

  public async getDropdownLabels(itemId: number, columnId: string) {
    try {
      const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
      const response: any = await sdkForUser.api(getDropdownLabelsQl(itemId, columnId));
      handleMondayError(response);
      return response?.data?.items[0]?.column_values[0]?.values ?? [];
    } catch (error: any) {
      logger.error("Error fetching dropdown labels", error);
      throw error;
    }
  }

  /** The raw value and settings of one of an item's columns, read fresh for an editor. */
  public async getEditableColumnState(itemId: number, columnId: string) {
    const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
    const response: any = await sdkForUser.api(getEditableColumnStateQl, {
      variables: { itemIds: [itemId], columnIds: [columnId] },
    });
    handleMondayError(response);
    const columnValue = response?.data?.items?.[0]?.column_values?.[0];
    if (!columnValue) throw new Error(`Column ${columnId} not found on item ${itemId}`);
    return {
      text: columnValue.text as string | null,
      value: columnValue.value as string | null,
      settingsStr: (columnValue.column?.settings_str as string | undefined) ?? "{}",
    };
  }

  /**
   * Writes one column of one item. `value` is the column's JSON, not yet stringified. Returns
   * the column as monday now has it, in getItemColumnValues' shape.
   */
  public async changeColumnValue(
    boardId: number,
    itemId: number,
    columnId: string,
    value: unknown,
  ): Promise<ColumnValueType | undefined> {
    const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
    const response: any = await sdkForUser.api(changeColumnValueQl, {
      variables: { boardId, itemId, columnId, value: JSON.stringify(value) },
    });
    handleMondayError(response);
    return response?.data?.change_column_value?.column_values?.[0];
  }

  public async getBoardTags(boardId: number): Promise<{ id: string; name: string }[]> {
    const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
    const response: any = await sdkForUser.api(getBoardTagsQl, {
      variables: { boardIds: [boardId] },
    });
    handleMondayError(response);
    return response?.data?.boards?.[0]?.tags ?? [];
  }

  public async createOrGetTag(
    boardId: number,
    name: string,
  ): Promise<{ id: string; name: string }> {
    const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
    const response: any = await sdkForUser.api(createOrGetTagQl, {
      variables: { name, boardId },
    });
    handleMondayError(response);
    return response?.data?.create_or_get_tag;
  }

  public async searchPeople(
    name: string,
  ): Promise<{ id: string; name: string; photo_thumb_small?: string }[]> {
    const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
    const response: any = await sdkForUser.api(searchPeopleQl, {
      variables: { name: name || null },
    });
    handleMondayError(response);
    return response?.data?.users ?? [];
  }

  private myTimeZone?: Promise<string | undefined>;

  /** The viewer's monday profile time zone (IANA name), fetched once per session. */
  public getMyTimeZone(): Promise<string | undefined> {
    this.myTimeZone ??= this.getMondayServerSdkForUser()
      .then((sdk) => sdk.api(getMyTimeZoneQl))
      .then((response: any) => {
        handleMondayError(response);
        return response?.data?.me?.time_zone_identifier || undefined;
      })
      .catch((error) => {
        // A later editor can try again; this one falls back to the browser's zone.
        this.myTimeZone = undefined;
        throw error;
      });
    return this.myTimeZone;
  }

  public async getUsersByIds(
    ids: number[],
  ): Promise<{ id: string; name: string; photo_thumb_small?: string }[]> {
    if (ids.length === 0) return [];
    const sdkForUser: MondayClientSdk = await this.getMondayServerSdkForUser();
    const response: any = await sdkForUser.api(getUsersByIdsQl, { variables: { ids } });
    handleMondayError(response);
    return response?.data?.users ?? [];
  }

  /** Opens monday's own upload dialog for a file column; requires boards:write. */
  public async triggerFilesUpload(boardId: number, itemId: number, columnId: string) {
    const monday: MondayClientSdk = await this.getMondayServerSdkForUser();
    return monday.execute("triggerFilesUpload", { boardId, itemId, columnId });
  }
}

export const MondayApiQueryInstance: MondayService = new MondayService();
